import React, { useState, useRef, useEffect, useMemo } from 'react';
import Select from 'react-select';
import {
  FormItem,
  FormQuestionItem,
  isPageBreak,
  QUESTION_TYPE_MAP_TRANSLATION,
  Question,
} from '../../types';
import './QuestionTypePopup.css';

interface QuestionTypePopupProps {
  allQuestions: Question[];
  currentFormQuestions?: FormItem[];
  onNewQuestion: () => void;
  onExistingQuestion: (question: Question, childQuestions: Question[]) => void;
  onAddPageBreak?: (position: number) => void;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
  /** Position at which to insert (for new question or page break). */
  position: number;
}

// Recursively find all descendant questions (children, grandchildren, etc.)
const findAllDescendants = (parentGuid: string, questions: Question[]): Question[] => {
  const children = questions.filter((q) => q.auto_generated_from_parent === parentGuid);
  return children.flatMap((child) => [child, ...findAllDescendants(child.guid, questions)]);
};

const QuestionTypePopup: React.FC<QuestionTypePopupProps> = ({
  allQuestions,
  currentFormQuestions = [],
  onNewQuestion,
  onExistingQuestion,
  onAddPageBreak,
  onClose,
  buttonRef,
  position,
}) => {
  const [showSelect, setShowSelect] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const stripHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // Merge allQuestions with currentFormQuestions, deduplicating by question_key
  const mergedQuestions = useMemo(() => {
    const questionMap = new Map<string, Question>();

    // First, add all questions from allQuestions (existing saved questions)
    allQuestions.forEach((q) => {
      const key = q.question_key?.toLowerCase() || q.guid;
      if (!questionMap.has(key)) {
        questionMap.set(key, q);
      }
    });

    // Then, add answerable questions from current form (page breaks are not selectable)
    currentFormQuestions
      .filter((q): q is FormQuestionItem => !isPageBreak(q))
      .forEach((q) => {
        const key = q.question_key?.toLowerCase() || q.guid;
        if (q.question_key && !questionMap.has(key)) {
          questionMap.set(key, q);
        }
      });

    return Array.from(questionMap.values());
  }, [allQuestions, currentFormQuestions]);

  const questionOptions = mergedQuestions.map((q) => {
    const plainText = stripHtml(q.text);
    return {
      value: q.guid,
      label: `${q.question_key || 'No Key'} - ${plainText}`,
      searchableText: `${q.question_key || ''} ${plainText}`.toLowerCase(),
      question: q,
    };
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, buttonRef]);

  const handleExistingQuestionClick = () => {
    setShowSelect(true);
  };

  const handleQuestionSelect = (question: Question) => {
    // Find all descendant questions (children, grandchildren, etc.)
    const childQuestions = findAllDescendants(question.guid, allQuestions);
    onExistingQuestion(question, childQuestions);
    onClose();
  };

  return (
    <div className='question-type-popup' ref={popupRef}>
      {!showSelect ? (
        <div className='popup-buttons'>
          <button className='popup-btn' onClick={onNewQuestion}>
            שאלה חדשה
          </button>
          <button className='popup-btn' onClick={handleExistingQuestionClick}>
            שאלה קיימת
          </button>
          {onAddPageBreak != null && (
            <button
              className='popup-btn'
              onClick={() => {
                onAddPageBreak(position);
                onClose();
              }}
            >
              {QUESTION_TYPE_MAP_TRANSLATION.page_break}
            </button>
          )}
        </div>
      ) : (
        <div className='popup-select-section'>
          <Select
            options={questionOptions}
            placeholder='בחר שאלה קיימת'
            onChange={(option) => option && handleQuestionSelect(option.question)}
            className='popup-question-select'

            filterOption={(option, inputValue) =>
              option.data.searchableText.includes(inputValue.toLowerCase())
            }
            autoFocus
            menuIsOpen
            isSearchable
          />
        </div>
      )}
    </div>
  );
};

export default QuestionTypePopup;
